import { useState, useRef, useEffect, useMemo } from "react";

import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useLocation, Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart, User, Briefcase, FileText, MapPin, Globe,
  ChevronRight, ChevronLeft, Plus, Trash2, Check,
  ChevronDown, Building2, Phone, Mail,
  Clock, GraduationCap, Award, Stethoscope, ShieldCheck, Search, Percent, Sparkles,
} from "lucide-react";


import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useGetLanguages, useGetProviderTypes, useGetVisitTypes, useGetCredentialTypes, useGetLocalities, useGetStates, useGetCodeTypes, API_BASE } from "@/lib/api-client-react";


/* ─── Constants ─────────────────────────────────────────────────────────── */

const SALUTATIONS = ["Dr.", "Mr.", "Mrs.", "Ms.", "Prof.", "Rev.", "Other"];
const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];
const DEFAULT_US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
  "VA", "WA", "WV", "WI", "WY", "DC",
];

/* ─── Types ──────────────────────────────────────────────────────────────── */

type Credential = {
  type: string; title: string; institution: string; year: string; description: string;
};
type Schedule = {
  dayOfWeek: string; startTime: string; endTime: string; isClosed: boolean;
};
const DEFAULT_SCHEDULES: Schedule[] = [
  { dayOfWeek: "Monday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: false },
  { dayOfWeek: "Tuesday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: false },
  { dayOfWeek: "Wednesday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: false },
  { dayOfWeek: "Thursday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: false },
  { dayOfWeek: "Friday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: false },
  { dayOfWeek: "Saturday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: true },
  { dayOfWeek: "Sunday", startTime: "09:00 AM", endTime: "05:00 PM", isClosed: true },
];

const TIME_OPTIONS = Array.from({ length: 48 }).map((_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  const ampm = h < 12 ? 'AM' : 'PM';
  const hour = h === 0 ? 12 : (h > 12 ? h - 12 : h);
  return `${hour.toString().padStart(2, '0')}:${m} ${ampm}`;
});

type Location = {
  isPrimary: boolean; isActive?: boolean; buildingNo: string; address1: string; address2: string; city: string;
  state: string; zipCode: string; phone: string; officePhone: string;
  fax: string; email: string; website: string; localityIds?: string[]; schedules: Schedule[];
};

type FormData = {
  practiceType: "individual" | "group";
  salutation: string; firstName: string; middleName: string; lastName: string;
  displayName: string; gender: string; headline: string;
  npiNumber: string; individualNpiNumber?: string; groupNpiNumber?: string;
  providerTypes: string[]; languages: string[]; localityIds: string[];
  statesLicensed: string[]; credentials: Credential[]; medicareCharge: string;
  synopsis: string; visitTypes: string[]; codeTypes?: string[];
  locations: Location[];
  zocdocUrl: string; webmdUrl: string; healthgradesUrl: string; usnewsUrl: string;
  socialLinks?: { platform: string; url: string }[];
};


/* ─── MultiSelect ────────────────────────────────────────────────────────── */

function MultiSelect({
  options = [], allOptions = [], value = [], onChange, placeholder, maxHeight = 260,
}: {
  options: any[]; allOptions?: any[]; value: string[]; onChange: (v: string[]) => void;
  placeholder: string; maxHeight?: number;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const normalizedOptions = (options || []).map((o: any) => {
    if (typeof o === "string") return { value: o, label: o, name: o, id: o };
    if (typeof o === "object" && o !== null) {
      const lbl = String(o.label || o.name || o.code || o.value || o.id || "").trim();
      const val = String(o.value || o.id || o.code || lbl).trim();
      return { value: val || lbl, label: lbl || val, name: lbl || val, id: val || lbl };
    }
    const str = String(o ?? "").trim();
    return { value: str, label: str, name: str, id: str };
  }).filter(o => Boolean(o.label));

  const normalizedAllOptions = (allOptions || []).map((o: any) => {
    if (typeof o === "string") return { value: o, label: o, name: o, id: o };
    if (typeof o === "object" && o !== null) {
      const val = String(o.value ?? o.id ?? o.name ?? o.label ?? "");
      const lbl = String(o.label ?? o.name ?? o.value ?? "");
      const name = String(o.name ?? o.label ?? o.value ?? "");
      const id = String(o.id ?? o.value ?? "");
      return { value: val, label: lbl, name, id };
    }
    return { value: String(o ?? ""), label: String(o ?? ""), name: String(o ?? ""), id: String(o ?? "") };
  });

  const filtered = normalizedOptions.filter(o =>
    o.label.toLowerCase().includes((search || "").toLowerCase())
  );

  const safeValue = Array.isArray(value) ? value : [];

  const isOptionSelected = (opt: any) => {
    const optValStr = String(opt.value ?? opt.id ?? opt.name ?? "").toLowerCase();
    const optLblStr = String(opt.label ?? opt.name ?? opt.value ?? "").toLowerCase();
    const optNameStr = String(opt.name ?? opt.label ?? opt.value ?? "").toLowerCase();
    const optIdStr = String(opt.id ?? opt.value ?? "").toLowerCase();

    return safeValue.some((v: any) => {
      if (!v) return false;
      let vStr = "";
      let vName = "";
      let vId = "";
      if (typeof v === "object" && v !== null) {
        vStr = String(v.value ?? v.id ?? v.name ?? v.code ?? "").toLowerCase();
        vName = String(v.name ?? v.label ?? v.code ?? "").toLowerCase();
        vId = String(v.id ?? v.value ?? "").toLowerCase();
      } else {
        vStr = String(v).toLowerCase();
        vName = vStr;
        vId = vStr;
      }

      return (
        vStr === optValStr ||
        vStr === optLblStr ||
        vStr === optNameStr ||
        vStr === optIdStr ||
        (vName && (vName === optValStr || vName === optLblStr || vName === optNameStr)) ||
        (vId && (vId === optValStr || vId === optIdStr))
      );
    });
  };

  const toggle = (opt: any) => {
    const optObj = typeof opt === "string" ? { value: opt, label: opt, name: opt, id: opt } : opt;
    const optValStr = String(optObj.value ?? optObj.id ?? optObj.name ?? "").toLowerCase();
    const optLblStr = String(optObj.label ?? optObj.name ?? optObj.value ?? "").toLowerCase();
    const optNameStr = String(optObj.name ?? optObj.label ?? optObj.value ?? "").toLowerCase();
    const optIdStr = String(optObj.id ?? optObj.value ?? "").toLowerCase();

    const isSelected = isOptionSelected(optObj);

    if (isSelected) {
      onChange(
        safeValue.filter((v: any) => {
          if (!v) return false;
          let vStr = "";
          let vName = "";
          let vId = "";
          if (typeof v === "object" && v !== null) {
            vStr = String(v.value ?? v.id ?? v.name ?? v.code ?? "").toLowerCase();
            vName = String(v.name ?? v.label ?? v.code ?? "").toLowerCase();
            vId = String(v.id ?? v.value ?? "").toLowerCase();
          } else {
            vStr = String(v).toLowerCase();
            vName = vStr;
            vId = vStr;
          }

          const matches = (
            vStr === optValStr ||
            vStr === optLblStr ||
            vStr === optNameStr ||
            vStr === optIdStr ||
            (vName && (vName === optValStr || vName === optLblStr || vName === optNameStr)) ||
            (vId && (vId === optValStr || vId === optIdStr))
          );
          return !matches;
        })
      );
    } else {
      const valToAdd = String(optObj.value ?? optObj.id ?? optObj.name ?? "");
      onChange([...safeValue, valToAdd]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-[40px] px-3 py-2 border border-input rounded-md bg-background text-sm text-left flex flex-wrap gap-1.5 items-center hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 transition-colors"
        >
          {safeValue.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            safeValue.map((v: any) => {
              const strV = String(v);
              const found = normalizedOptions.find((o: any) =>
                o.value === strV ||
                o.value.toLowerCase() === strV.toLowerCase() ||
                o.label === strV ||
                o.label.toLowerCase() === strV.toLowerCase() ||
                (o.name && o.name.toLowerCase() === strV.toLowerCase()) ||
                (o.id && o.id.toLowerCase() === strV.toLowerCase())
              ) || normalizedAllOptions.find((o: any) =>
                o.value === strV ||
                o.value.toLowerCase() === strV.toLowerCase() ||
                o.label === strV ||
                o.label.toLowerCase() === strV.toLowerCase() ||
                (o.name && o.name.toLowerCase() === strV.toLowerCase()) ||
                (o.id && o.id.toLowerCase() === strV.toLowerCase())
              );
              const label = found ? found.label : strV;
              return (
                <Badge key={strV} variant="secondary" className="text-xs rounded-full px-2.5 py-0.5 bg-primary/10 text-primary border border-primary/20 flex items-center gap-1">
                  <span>{label}</span>
                  <span
                    className="ml-0.5 cursor-pointer hover:text-destructive font-bold text-xs"
                    onClick={e => { e.stopPropagation(); toggle(found || { value: strV, label: strV }); }}
                  >×</span>
                </Badge>
              );
            })
          )}
          <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <div className="p-2 border-b">
          <Input
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
        </div>
        <div className="overflow-y-auto" style={{ maxHeight }}>
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No results</p>
          ) : filtered.map((opt: any) => {
            const checked = isOptionSelected(opt);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors text-left"
              >
                <div className={cn(
                  "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                  checked ? "bg-primary border-primary" : "border-input"
                )}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </div>
                {opt.label}
              </button>
            );
          })}
        </div>
        {value.length > 0 && (
          <div className="p-2 border-t">
            <button type="button" onClick={() => onChange([])} className="text-xs text-muted-foreground hover:text-foreground">
              Clear all ({value.length} selected)
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function SingleLocalitySelect({
  value,
  onChange,
  options,
  placeholder = "Select locality..."
}: {
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedOption = options.find((o) => o.value === value);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full h-11 justify-between rounded-xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-normal hover:bg-slate-50 dark:hover:bg-slate-900 text-sm shadow-2xs transition-all px-3.5"
        >
          {selectedOption ? (
            <span className="font-medium text-slate-900 dark:text-slate-100 truncate pr-2">
              {selectedOption.label}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden"
        align="start"
        sideOffset={6}
      >
        <div className="p-2 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search locality..."
              className="pl-8 h-9 text-xs rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950"
            />
          </div>
        </div>
        <div className="max-h-60 overflow-y-auto p-1.5 space-y-0.5">
          {filteredOptions.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              No locality found
            </div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={`w-full text-left flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-medium transition-colors ${isSelected
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                >
                  <span className="truncate pr-2">{opt.label}</span>
                  {isSelected && <Check className="w-4 h-4 text-primary shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ─── Step Indicator ─────────────────────────────────────────────────────── */

const STEPS = [
  { label: "Personal", icon: User },
  { label: "Professional", icon: Briefcase },
  { label: "About", icon: FileText },
  { label: "Locations", icon: MapPin },
  { label: "Fee Schedule", icon: Percent },
  { label: "Online Presence", icon: Globe },
];


function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = step.icon;
        return (
          <div key={i} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <div className={cn(
                "w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                done ? "bg-primary border-primary text-white"
                  : active ? "bg-white border-primary text-primary shadow-md shadow-primary/20"
                    : "bg-white border-border text-muted-foreground"
              )}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={cn(
                "text-[11px] font-medium whitespace-nowrap hidden sm:block",
                active ? "text-primary" : done ? "text-foreground" : "text-muted-foreground"
              )}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={cn(
                "w-10 sm:w-16 h-0.5 mb-5 transition-all duration-500",
                i < current ? "bg-primary" : "bg-border"
              )} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Field helpers ──────────────────────────────────────────────────────── */

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive mt-1">{message}</p>;
}

function FieldLabel({ children, required, className }: { children: React.ReactNode; required?: boolean; className?: string }) {
  return (
    <Label className={cn("text-sm font-medium text-foreground", className)}>
      {children}{required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );
}


/* ─── Main Component ─────────────────────────────────────────────────────── */

export default function ProviderRegister() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [isDisplayNameManuallyEdited, setIsDisplayNameManuallyEdited] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const form = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      practiceType: "individual",
      salutation: "", firstName: "", middleName: "", lastName: "",
      displayName: "", gender: "", headline: "",
      npiNumber: "", providerTypes: [], languages: [], localityIds: [], statesLicensed: [],
      credentials: [], medicareCharge: "",
      synopsis: "", visitTypes: [],
      locations: [{
        isPrimary: true, buildingNo: "", address1: "", address2: "", city: "", state: "",
        zipCode: "", phone: "", officePhone: "", fax: "", email: "", website: "", localityIds: [], schedules: [...DEFAULT_SCHEDULES],
      }],

      zocdocUrl: "", webmdUrl: "", healthgradesUrl: "", usnewsUrl: "",
      socialLinks: [],
    },
  });

  const { register, control, trigger, watch, setValue, getValues, formState: { errors } } = form;
  const { fields: socialFields, append: addSocial, remove: removeSocial } = useFieldArray({ control, name: "socialLinks" });

  const [categoryRates, setCategoryRates] = useState<Record<string, any>>({});
  const [selectedRateLocationIndex, setSelectedRateLocationIndex] = useState<number>(-1);

  const getLocationKeys = (loc: any, idx: number): string[] => {
    const keys: string[] = [];
    if (loc?.id) keys.push(String(loc.id));
    keys.push(`loc_${idx}`);
    const addr = loc?.address1 || loc?.street1;
    if (addr && String(addr).trim()) keys.push(String(addr).trim());
    return keys;
  };

  const getEffectiveRateForCodeType = (codeTypeName: string, codeTypeId: string): { rate: number; isCustom: boolean } => {
    const globalRate = categoryRates[codeTypeName] ?? categoryRates[codeTypeId] ?? 100;
    if (selectedRateLocationIndex === -1) {
      return { rate: Number(globalRate), isCustom: false };
    }

    const locFieldsList = form.getValues("locations") || [];
    const loc = locFieldsList[selectedRateLocationIndex];
    if (!loc) return { rate: Number(globalRate), isCustom: false };

    const keys = getLocationKeys(loc, selectedRateLocationIndex);
    for (const k of keys) {
      if (categoryRates[k] && typeof categoryRates[k] === "object") {
        const val = categoryRates[k][codeTypeName] ?? categoryRates[k][codeTypeId];
        if (val !== undefined && val !== null) {
          return { rate: Number(val), isCustom: true };
        }
      }
    }
    return { rate: Number(globalRate), isCustom: false };
  };

  const handleUpdateCodeTypeRate = (codeTypeName: string, codeTypeId: string, newRate: number) => {
    setCategoryRates(prev => {
      const updated = { ...prev };
      if (selectedRateLocationIndex === -1) {
        updated[codeTypeName] = newRate;
        if (codeTypeId && isUUID(codeTypeId)) {
          delete updated[codeTypeId];
        }
      } else {
        const locFieldsList = form.getValues("locations") || [];
        const loc = locFieldsList[selectedRateLocationIndex];
        if (loc) {
          const keys = getLocationKeys(loc, selectedRateLocationIndex);
          let existingSubObj: Record<string, any> = {};
          for (const k of keys) {
            if (updated[k] && typeof updated[k] === "object") {
              existingSubObj = { ...updated[k] };
              break;
            }
          }
          existingSubObj[codeTypeName] = newRate;
          keys.forEach(k => {
            updated[k] = { ...(updated[k] || {}), ...existingSubObj };
          });
        }
      }
      return updated;
    });
  };

  const handleResetLocationRatesToDefault = (locIdx: number) => {
    const locFieldsList = form.getValues("locations") || [];
    const loc = locFieldsList[locIdx];
    if (!loc) return;
    const keys = getLocationKeys(loc, locIdx);
    setCategoryRates(prev => {
      const updated = { ...prev };
      keys.forEach(k => {
        delete updated[k];
      });
      return updated;
    });
  };

  const [inlineNpiLoading, setInlineNpiLoading] = useState(false);
  const [inlineNpiMsg, setInlineNpiMsg] = useState<{ type: "success" | "error" | "info"; text: string } | null>(null);
  const [prefilledBanner, setPrefilledBanner] = useState<string | null>(null);


  const populateFromNpiDetails = (details: any) => {
    if (!details) return;
    if (details.practiceType) setValue("practiceType", details.practiceType);
    if (details.salutation) setValue("salutation", details.salutation);
    if (details.firstName) setValue("firstName", details.firstName);
    if (details.middleName) setValue("middleName", details.middleName);
    if (details.lastName) setValue("lastName", details.lastName);
    if (details.displayName) setValue("displayName", details.displayName);
    if (details.gender) setValue("gender", details.gender);
    if (details.headline) setValue("headline", details.headline);
    if (details.npiNumber) setValue("npiNumber", details.npiNumber);
    if (details.medicareCharge) setValue("medicareCharge", details.medicareCharge);
    if (details.synopsis) setValue("synopsis", details.synopsis);
    if (details.zocdocUrl) setValue("zocdocUrl", details.zocdocUrl);
    if (details.webmdUrl) setValue("webmdUrl", details.webmdUrl);
    if (details.healthgradesUrl) setValue("healthgradesUrl", details.healthgradesUrl);
    if (details.usnewsUrl) setValue("usnewsUrl", details.usnewsUrl);

    if (Array.isArray(details.statesLicensed) && details.statesLicensed.length > 0) {
      setValue("statesLicensed", details.statesLicensed);
    }
    if (Array.isArray(details.providerTypes) && details.providerTypes.length > 0) {
      setValue("providerTypes", details.providerTypes);
    }
    if (Array.isArray(details.visitTypes) && details.visitTypes.length > 0) {
      setValue("visitTypes", details.visitTypes);
    }
    if (Array.isArray(details.languages) && details.languages.length > 0) {
      setValue("languages", details.languages);
    }
    if (Array.isArray(details.localityIds) && details.localityIds.length > 0) {
      setValue("localityIds", details.localityIds);
    }
    if (Array.isArray(details.credentials) && details.credentials.length > 0) {
      setValue("credentials", details.credentials);
    }
    if (Array.isArray(details.locations) && details.locations.length > 0) {
      setValue("locations", details.locations);
    }
    setPrefilledBanner(`Profile pre-filled for NPI #${details.npiNumber}`);
  };

  useEffect(() => {
    const rawDetails = localStorage.getItem("npiProviderDetails");
    const regNpi = localStorage.getItem("registerNpi");
    const regPracticeType = localStorage.getItem("registerPracticeType");
    if (regPracticeType) {
      setValue("practiceType", regPracticeType as any);
    }
    if (regNpi) {
      setValue("npiNumber", regNpi);
    }
    if (rawDetails) {
      try {
        const parsed = JSON.parse(rawDetails);
        populateFromNpiDetails(parsed);
      } catch (e) {
        console.error("Failed to parse prefilled NPI details", e);
      }
    }
  }, []);

  const fetchAndFillNpi = async () => {
    const npiVal = (getValues("npiNumber") || "").trim();
    if (!npiVal || npiVal.length !== 10) {
      setInlineNpiMsg({ type: "error", text: "Please enter a valid 10-digit NPI number" });
      return;
    }

    setInlineNpiLoading(true);
    setInlineNpiMsg(null);
    try {
      const res = await fetch(`${API_BASE}/auth/npi-lookup/${encodeURIComponent(npiVal)}`);
      const json = await res.json();
      if (json.alreadyConnected || (!json.success && json.error?.includes("associated with another provider"))) {
        setInlineNpiMsg({
          type: "error",
          text: json.error || "This NPI number is associated with another provider account. Please log in with that account."
        });
        return;
      }
      if (json.success && json.found && json.data) {
        populateFromNpiDetails(json.data);
        localStorage.setItem("npiProviderDetails", JSON.stringify(json.data));
        localStorage.setItem("registerNpi", npiVal);
        setInlineNpiMsg({
          type: "success",
          text: "Found provider details in Database. Form pre-filled!"
        });
      } else {
        setInlineNpiMsg({
          type: "error",
          text: json.message || "No provider record found in database for this NPI number."
        });
      }
    } catch (e: any) {

      setInlineNpiMsg({
        type: "error",
        text: "Failed to connect for NPI search."
      });
    } finally {
      setInlineNpiLoading(false);
    }
  };

  const { data: statesData = [] } = useGetStates();

  const US_STATES = statesData.length > 0 ? statesData.map((s: any) => typeof s === 'string' ? s : String(s?.code || s?.name || '')) : DEFAULT_US_STATES;
  const { data: languagesData = [] } = useGetLanguages();
  const { data: providerTypesData = [] } = useGetProviderTypes();
  const { data: visitTypesData = [] } = useGetVisitTypes();
  const { data: credentialTypesData = [] } = useGetCredentialTypes();
  const { data: codeTypesData = [] } = useGetCodeTypes();
  const primaryLocationState = watch("locations.0.state");
  const selectedStates = watch("statesLicensed");
  const localityStateFilter = primaryLocationState || (selectedStates && selectedStates.length > 0 ? selectedStates : undefined);
  const { data: masterLocalitiesData = [] } = useGetLocalities();
  const { data: localitiesData = [] } = useGetLocalities(localityStateFilter);

  const activeCodeTypes = useMemo(() => {
    if (codeTypesData && codeTypesData.length > 0) {
      return codeTypesData
        .filter((ct: any) => ct && ct.isActive !== false)
        .map((ct: any) => {
          if (typeof ct === "string") return { id: ct, name: ct, code: ct };
          const name = String(ct.name || ct.code || ct.type || ct.label || ct.id || "").trim();
          const code = String(ct.code || ct.name || name).trim();
          const id = String(ct.id || code || name).trim();
          return { id: id || name, name: name || code || id, code: code || name };
        })
        .filter(ct => Boolean(ct.name));
    }
    return [
      { id: "PFS", name: "PFS", code: "PFS" },
      { id: "CLFS", name: "CLFS", code: "CLFS" },
      { id: "DFS", name: "DFS", code: "DFS" },
      { id: "NADAC", name: "NADAC", code: "NADAC" },
    ];
  }, [codeTypesData]);

  const CODE_TYPE_OPTIONS = useMemo(() => {
    return activeCodeTypes.map((ct: any) => {
      const labelStr = ct.name || ct.code || ct.id;
      const valStr = ct.id || ct.code || labelStr;
      return {
        value: valStr,
        label: labelStr,
        name: labelStr,
        id: valStr
      };
    });
  }, [activeCodeTypes]);

  const isUUID = (str: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(str || ""));

  const selectedCodeTypeIds = watch("codeTypes") || [];
  const displayedCodeTypes = useMemo(() => {
    const items: any[] = [];
    const seenKeys = new Set<string>();

    const markSeen = (id: string, name: string, code?: string) => {
      if (id) seenKeys.add(String(id).toLowerCase());
      if (name) seenKeys.add(String(name).toLowerCase());
      if (code) seenKeys.add(String(code).toLowerCase());
    };

    const isSeen = (id: string, name: string, code?: string) => {
      return (
        (id && seenKeys.has(String(id).toLowerCase())) ||
        (name && seenKeys.has(String(name).toLowerCase())) ||
        (code && seenKeys.has(String(code).toLowerCase()))
      );
    };

    const addItem = (id: string, name: string, code?: string) => {
      const match = activeCodeTypes.find((ct: any) =>
        (id && ct.id.toLowerCase() === String(id).toLowerCase()) ||
        (name && ct.name.toLowerCase() === String(name).toLowerCase()) ||
        (code && ct.code.toLowerCase() === String(code).toLowerCase()) ||
        (id && ct.name.toLowerCase() === String(id).toLowerCase()) ||
        (name && ct.id.toLowerCase() === String(name).toLowerCase())
      );

      const finalId = match?.id || id || name;
      const finalName = match?.name || (isUUID(name) ? (match?.name || name) : name);
      const finalCode = match?.code || code || finalName;

      if (isUUID(finalName) && !match) {
        return;
      }

      if (isSeen(finalId, finalName, finalCode)) {
        return;
      }

      markSeen(finalId, finalName, finalCode);
      if (match) {
        markSeen(match.id, match.name, match.code);
      }

      items.push({ id: finalId, name: finalName, code: finalCode });
    };

    if (selectedCodeTypeIds && selectedCodeTypeIds.length > 0) {
      selectedCodeTypeIds.forEach((sId: any) => {
        if (!sId) return;
        const s = typeof sId === "object" ? String(sId.name || sId.id || sId.value || "") : String(sId);
        if (!s) return;
        const match = activeCodeTypes.find((ct: any) => ct.name.toLowerCase() === s.toLowerCase() || String(ct.id).toLowerCase() === s.toLowerCase());
        if (match) {
          addItem(match.id, match.name, match.code);
        } else if (!isUUID(s)) {
          addItem(s, s, s);
        }
      });
    }

    if (categoryRates && typeof categoryRates === "object") {
      Object.keys(categoryRates).forEach(k => {
        if (typeof categoryRates[k] !== "object" && categoryRates[k] !== undefined && categoryRates[k] !== null && !k.startsWith("loc_") && k !== "default") {
          const match = activeCodeTypes.find((ct: any) => ct.id.toLowerCase() === k.toLowerCase() || ct.name.toLowerCase() === k.toLowerCase());
          if (match) {
            addItem(match.id, match.name, match.code);
          } else if (!isUUID(k)) {
            addItem(k, k, k);
          }
        }
      });
    }

    if (items.length === 0 && activeCodeTypes && activeCodeTypes.length > 0) {
      activeCodeTypes.forEach((ct: any) => addItem(ct.id, ct.name, ct.code));
    }

    return items;
  }, [activeCodeTypes, selectedCodeTypeIds, categoryRates]);

  const LANGUAGES = (languagesData || []).map((l: any) => {
    if (typeof l === "string") return { value: l, label: l };
    const val = String(l?.id ?? l?.value ?? l?.name ?? l?.label ?? "");
    const lbl = String(l?.name ?? l?.label ?? l?.value ?? val);
    return { value: val, label: lbl };
  });

  const PROVIDER_TYPES = (providerTypesData || []).map((p: any) => {
    if (typeof p === "string") return { value: p, label: p };
    const val = String(p?.id ?? p?.value ?? p?.name ?? p?.label ?? "");
    const lbl = String(p?.name ?? p?.label ?? p?.value ?? val);
    return { value: val, label: lbl };
  });

  const VISIT_TYPES_OPTIONS = (visitTypesData || []).map((v: any) => {
    if (typeof v === "string") return { value: v, label: v };
    const val = String(v?.id ?? v?.value ?? v?.name ?? v?.label ?? "");
    const lbl = String(v?.name ?? v?.label ?? v?.value ?? val);
    return { value: val, label: lbl };
  });

  const CREDENTIAL_TYPES = (credentialTypesData || []).map((c: any) => {
    if (typeof c === "string") return { value: c, label: c };
    const val = String(c?.id ?? c?.value ?? c?.name ?? c?.label ?? "");
    const lbl = String(c?.name ?? c?.label ?? c?.value ?? val);
    return { value: val, label: lbl };
  });

  const US_STATES_OPTIONS = US_STATES.map((s: string) => ({ value: s, label: s }));
  const formatLocalityLabel = (loc: any) => {
    if (!loc) return "";
    const locNum = loc.localityNumber || loc.code;
    const locState = loc.state;
    const bracketVal = locNum ? locNum : locState;
    return `${loc.name || loc.label || ''}${bracketVal ? ` (${bracketVal})` : ''}`;
  };

  const MASTER_LOCALITIES = (masterLocalitiesData || []).map((loc: any) => ({
    value: String(loc?.id ?? loc?.localityNumber ?? loc?.code ?? loc?.name ?? ""),
    label: formatLocalityLabel(loc)
  }));

  const LOCALITIES = (localitiesData || []).map((loc: any) => ({
    value: String(loc?.id ?? loc?.localityNumber ?? loc?.code ?? loc?.name ?? ""),
    label: formatLocalityLabel(loc)
  }));


  const getProviderTypeName = (val: string) => {
    const pt = (providerTypesData || []).find((p: any) => String(p?.id ?? p?.name ?? p) === String(val) || p?.name === val);
    return pt ? (pt.name || pt.label || String(pt)) : val;
  };

  const getLocalityLabel = (locId: string) => {
    const locObj = LOCALITIES.find((l: any) => l.value === locId) || MASTER_LOCALITIES.find((l: any) => l.value === locId);
    if (locObj) return locObj.label;
    const rawLoc = (masterLocalitiesData || []).find((loc: any) => String(loc?.id ?? loc?.name ?? loc) === String(locId) || loc?.name === locId);
    if (rawLoc) return `${rawLoc.name} (${rawLoc.state}${rawLoc.city ? ' - ' + rawLoc.city : ''})`;
    return locId;
  };



  const { fields: credFields, append: addCred, remove: removeCred } = useFieldArray({ control, name: "credentials" });
  const { fields: locFields, append: addLoc, remove: removeLoc } = useFieldArray({ control, name: "locations" });

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const STEP_FIELDS: (keyof FormData)[][] = [
    ["firstName", "lastName", "displayName"],
    ["codeTypes"],
    [],
    ["localityIds"],
    [],
    [],
  ];



  const isStepValid = () => {
    const fields = STEP_FIELDS[step];
    if (!fields || fields.length === 0) return true;

    // Check if any of the fields in this step have errors
    const hasErrors = fields.some(field => errors[field]);
    if (hasErrors) return false;

    // Check if all required fields have a value
    const values = watch(fields as any);
    if (!values) return false;
    if (Array.isArray(values)) {
      return values.every(v => {
        if (v === undefined || v === null) return false;
        if (Array.isArray(v)) return v.length > 0;
        return v.toString().trim().length > 0;
      });
    }
    return values !== undefined && values !== null && values.toString().trim().length > 0;
  };

  const goNext = async () => {
    const valid = await trigger(STEP_FIELDS[step] as any);
    if (!valid) return;
    setStep(s => s + 1);
    scrollTop();
  };

  const goBack = () => { setStep(s => s - 1); scrollTop(); };

  const onSubmit = async () => {
    const valid = await trigger(STEP_FIELDS[step] as any);
    if (!valid) return;

    const email = localStorage.getItem("registerEmail");
    if (!email) {
      alert("Registration session expired or email not found. Please start over.");
      setLocation("/register");
      return;
    }

    setLoading(true);
    try {
      const vals = getValues();
      const firstRate = Object.values(categoryRates)[0];
      const medicareChargeVal = vals.medicareCharge ? (vals.medicareCharge.includes('%') ? vals.medicareCharge : `${vals.medicareCharge}%`) : (firstRate ? `${firstRate}%` : "100%");

      const res = await fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...vals,
          email,
          phone: vals.locations?.[0]?.phone || "",
          role: "PROVIDER",
          medicareCharge: medicareChargeVal,
          categoryRates: Object.keys(categoryRates).length > 0 ? categoryRates : { General: 100 },
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);

      if (json.data?.token) {
        localStorage.setItem("token", json.data.token);
        localStorage.setItem("user", JSON.stringify(json.data.user));
      }

      localStorage.setItem("practiceType", vals.practiceType);
      localStorage.removeItem("registerEmail"); // Clean up

      setLocation("/provider/dashboard");
    } catch (err: any) {
      alert(err.message || "Failed to register");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-6">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Registration Submitted!</h2>
          <p className="text-muted-foreground mb-6">
            Your provider profile is under review. Our team will be in touch once approved — typically within 1–2 business days.
          </p>
          <Button
            onClick={() => setLocation("/login")}
            className="bg-gradient-to-r from-primary to-secondary text-white rounded-xl h-11 px-8"
          >
            Go to Sign In
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2" ref={topRef}>
      <div className="hidden lg:flex flex-col bg-gradient-to-br from-secondary via-secondary/90 to-primary p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.08),transparent_60%)]" />
        <div className="flex items-center gap-3 relative z-10">
          <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl">StraightDoc</span>
        </div>
        <div className="flex-1 flex flex-col justify-center relative z-10">
          <motion.h1 initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="text-4xl font-bold mb-4">
            Join the future of healthcare
          </motion.h1>
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="text-white/70 text-lg mb-8">
            Create your free account to access verified providers, instant cost estimates, and seamless care coordination.
          </motion.p>
          <div className="bg-white/10 rounded-2xl p-5 backdrop-blur border border-white/20">
            <p className="text-sm font-medium mb-4">Why choose StraightDoc?</p>
            {["Passwordless secure access", "Find top-rated professionals", "Real-time cost estimates", "HIPAA-secure environment"].map((item, i) => (
              <motion.div key={item} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }} className="flex items-center gap-3 text-white/90 mb-3 last:mb-0">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col p-6 sm:p-10 bg-background relative overflow-y-auto">
        <div className="w-full max-w-xl mx-auto relative z-10 my-auto pt-4 pb-12">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">StraightDoc</span>
            </div>
            <Link href="/login" className="text-sm text-primary font-medium hover:underline">Sign in</Link>
          </div>
          {/* Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-medium px-3 py-1.5 rounded-full mb-3">
              <Stethoscope className="w-4 h-4" />
              Provider Registration
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">Join the StraightDoc Network</h1>
            <p className="text-muted-foreground">Complete your professional profile in {STEPS.length} simple steps</p>
          </div>

          {prefilledBanner && (
            <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5" />
                </div>
                <span>{prefilledBanner}</span>
              </div>
              <button
                type="button"
                onClick={() => setPrefilledBanner(null)}
                className="text-xs text-emerald-700 hover:text-emerald-900 underline font-semibold"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Step Indicator */}
          <StepIndicator current={step} />

          {/* Form Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.22 }}
              className="bg-white rounded-2xl border border-border shadow-sm p-6 sm:p-8"
            >
              {/* ── Step 0: Personal Info ── */}
              {step === 0 && (
                <div className="space-y-5">
                  <SectionHeader icon={User} title="Personal Information" subtitle="Your name and basic personal details" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel>Salutation</FieldLabel>
                      <Controller
                        control={control} name="salutation"
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{SALUTATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div>
                      <FieldLabel required>First Name</FieldLabel>
                      <Input
                        {...register("firstName", {
                          required: "First name is required",
                          onChange: (e) => {
                            if (!isDisplayNameManuallyEdited) {
                              const vals = getValues();
                              const newFirst = e.target.value;
                              setValue("displayName", `${newFirst} ${vals.lastName}`.trim(), { shouldValidate: true });
                            }
                          }
                        })}
                        placeholder="Jane" className="mt-1.5"
                      />
                      <FieldError message={errors.firstName?.message} />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel>Middle Name</FieldLabel>
                      <Input {...register("middleName")} placeholder="M." className="mt-1.5" />
                    </div>
                    <div>
                      <FieldLabel required>Last Name</FieldLabel>
                      <Input
                        {...register("lastName", {
                          required: "Last name is required",
                          onChange: (e) => {
                            if (!isDisplayNameManuallyEdited) {
                              const vals = getValues();
                              const newLast = e.target.value;
                              setValue("displayName", `${vals.firstName} ${newLast}`.trim(), { shouldValidate: true });
                            }
                          }
                        })}
                        placeholder="Doe" className="mt-1.5"
                      />
                      <FieldError message={errors.lastName?.message} />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>Display Name</FieldLabel>
                      <Input
                        {...register("displayName", { required: "Display name is required" })}
                        placeholder="Dr. Jane Smith, MD" className="mt-1.5"
                      />
                      <FieldError message={errors.displayName?.message} />
                    </div>
                    <div>
                      <FieldLabel>Gender</FieldLabel>
                      <Controller
                        control={control} name="gender"
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select gender" /></SelectTrigger>
                            <SelectContent>{GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <FieldLabel>Professional Headline</FieldLabel>
                      <Input
                        {...register("headline")}
                        placeholder="Board-certified Cardiologist · 15+ years" className="mt-1.5"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 1: Professional Details ── */}
              {step === 1 && (
                <div className="space-y-6">
                  <SectionHeader icon={Briefcase} title="Professional Details" subtitle="Licensing, credentials and specialties" />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel>Specialties</FieldLabel>
                      <div className="mt-1.5">
                        <Controller
                          control={control} name="providerTypes"
                          render={({ field }) => (
                            <MultiSelect options={PROVIDER_TYPES} value={field.value} onChange={field.onChange} placeholder="Select specialties" />
                          )}
                        />
                      </div>
                    </div>
                    <div>
                      <FieldLabel required>Billing Code Types</FieldLabel>
                      <div className="mt-1.5">
                        <Controller
                          control={control} name="codeTypes"
                          rules={{ required: "Billing Code Types is required" }}
                          render={({ field }) => (
                            <MultiSelect
                              options={CODE_TYPE_OPTIONS}
                              value={field.value || []}
                              onChange={(newVal: string[]) => {
                                field.onChange(newVal);
                                setCategoryRates(prev => {
                                  const updated = { ...prev };
                                  newVal.forEach(ct => {
                                    const match = activeCodeTypes.find((act: any) => act.id === ct || act.name === ct);
                                    const ctName = match?.name || ct;
                                    if (!isUUID(ctName) && updated[ctName] === undefined) {
                                      updated[ctName] = 100;
                                    }
                                    if (match?.id && isUUID(match.id)) {
                                      delete updated[match.id];
                                    }
                                  });
                                  return updated;
                                });
                              }}
                              placeholder="Select billing code types"
                            />
                          )}
                        />
                      </div>
                      <FieldError message={errors.codeTypes?.message} />
                    </div>
                  </div>

                  <div>
                    <FieldLabel>Languages Spoken</FieldLabel>
                    <div className="mt-1.5">
                      <Controller
                        control={control} name="languages"
                        render={({ field }) => (
                          <MultiSelect options={LANGUAGES} value={field.value} onChange={field.onChange} placeholder="Select languages" />
                        )}
                      />
                    </div>
                  </div>

                  {/* Credentials */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <FieldLabel>Credentials</FieldLabel>
                        <p className="text-xs text-muted-foreground mt-0.5">Education, work experience, awards, certifications</p>
                      </div>
                      <Button
                        type="button" variant="outline" size="sm"
                        className="rounded-lg text-xs h-8"
                        onClick={() => addCred({ type: "Education", title: "", institution: "", year: "", description: "" })}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {credFields.length === 0 && (
                        <div className="border border-dashed border-border rounded-xl p-6 text-center text-sm text-muted-foreground">
                          <GraduationCap className="w-8 h-8 mx-auto mb-2 opacity-40" />
                          No credentials added yet. Click "Add" to get started.
                        </div>
                      )}
                      {credFields.map((cred, i) => (
                        <div key={cred.id} className="border border-border rounded-xl p-4 bg-slate-50/50 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Award className="w-4 h-4 text-primary" />
                              <span className="text-sm font-medium">Credential #{i + 1}</span>
                            </div>
                            <button type="button" onClick={() => removeCred(i)} className="text-muted-foreground hover:text-destructive transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid sm:grid-cols-3 gap-3">
                            <div>
                              <FieldLabel>Type</FieldLabel>
                              <Controller
                                control={control} name={`credentials.${i}.type`}
                                render={({ field }) => (
                                  <Select value={field.value} onValueChange={field.onChange}>
                                    <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                                    <SelectContent>{CREDENTIAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                                  </Select>
                                )}
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <FieldLabel>Title / Degree</FieldLabel>
                              <Input {...register(`credentials.${i}.title`)} placeholder="MD, Board Certification, Excellence Award…" className="mt-1 h-9 text-sm" />
                            </div>
                          </div>
                          <div className="grid sm:grid-cols-3 gap-3">
                            <div className="sm:col-span-2">
                              <FieldLabel>Institution / Organization</FieldLabel>
                              <Input {...register(`credentials.${i}.institution`)} placeholder="Harvard Medical School" className="mt-1 h-9 text-sm" />
                            </div>
                            <div>
                              <FieldLabel>Year</FieldLabel>
                              <Input {...register(`credentials.${i}.year`)} placeholder="2012" maxLength={4} className="mt-1 h-9 text-sm" />
                            </div>
                          </div>
                          <div>
                            <FieldLabel>Description (optional)</FieldLabel>
                            <Textarea {...register(`credentials.${i}.description`)} placeholder="Brief description…" rows={2} className="mt-1 text-sm resize-none" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 2: About & Practice ── */}
              {step === 2 && (
                <div className="space-y-6">
                  <SectionHeader icon={FileText} title="About & Practice" subtitle="Help patients understand your practice" />

                  <div>
                    <FieldLabel>Synopsis / About</FieldLabel>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">Write a compelling bio visible to patients (200–600 characters recommended)</p>
                    <Textarea
                      {...register("synopsis")}
                      placeholder="I am a board-certified cardiologist with over 15 years of experience treating patients with heart disease, hypertension, and heart failure. My practice focuses on preventive cardiology and personalized treatment plans…"
                      rows={6}
                      className="resize-none"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{watch("synopsis").length} characters</p>
                  </div>

                  <div>
                    <FieldLabel>Visit Types Offered</FieldLabel>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">Select all appointment types you offer</p>
                    <Controller
                      control={control} name="visitTypes"
                      render={({ field }) => (
                        <MultiSelect options={VISIT_TYPES_OPTIONS} value={field.value} onChange={field.onChange} placeholder="Select visit types" />
                      )}
                    />
                  </div>
                </div>
              )}

              {/* ── Step 3: Locations ── */}
              {step === 3 && (
                <div className="space-y-6">
                  <SectionHeader icon={MapPin} title="Practice Locations" subtitle="Add all locations where you see patients" />

                  <div className="space-y-4">
                    {locFields.map((loc, i) => (
                      <div key={loc.id} className="border border-border rounded-2xl overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-border">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-primary" />
                            <span className="font-medium text-sm">
                              {i === 0 ? "Primary Location" : `Location ${i + 1}`}
                            </span>
                            {i === 0 && <Badge className="text-xs bg-primary/10 text-primary border-0">Primary</Badge>}
                          </div>
                          {i > 0 && (
                            <button type="button" onClick={() => removeLoc(i)} className="text-muted-foreground hover:text-destructive transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <div className="p-4 space-y-3">
                          <div className="grid sm:grid-cols-3 gap-3">
                            <div>
                              <FieldLabel>Building No.</FieldLabel>
                              <Input {...register(`locations.${i}.buildingNo`)} placeholder="Bldg A, Suite 400" className="mt-1.5" />
                            </div>
                            <div className="sm:col-span-2">
                              <FieldLabel required>Address Line 1</FieldLabel>
                              <Input {...register(`locations.${i}.address1`, { required: "Address Line 1 is required" })} placeholder="123 Medical Center Blvd" className="mt-1.5" />
                            </div>
                          </div>
                          <div>
                            <FieldLabel>Address Line 2</FieldLabel>
                            <Input {...register(`locations.${i}.address2`)} placeholder="Suite 400" className="mt-1.5" />
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                              <FieldLabel required>City</FieldLabel>
                              <Input {...register(`locations.${i}.city`, { required: "City is required" })} placeholder="New York" className="mt-1.5" />
                            </div>
                            <div>
                              <FieldLabel required>State</FieldLabel>
                              <Controller
                                control={control} name={`locations.${i}.state`}
                                rules={{ required: "State is required" }}
                                render={({ field }) => (
                                  <Select
                                    value={field.value}
                                    onValueChange={(newState) => {
                                      field.onChange(newState);
                                      const currentLocalityIds = getValues(`locations.${i}.localityIds`) || [];
                                      const validIdsForNewState = (masterLocalitiesData || [])
                                        .filter((loc: any) => loc.state === newState)
                                        .map((loc: any) => String(loc?.id ?? loc?.localityNumber ?? loc?.code ?? loc?.name ?? ""));
                                      const filteredLocalityIds = currentLocalityIds.filter((id: string) => validIdsForNewState.includes(id));
                                      setValue(`locations.${i}.localityIds`, filteredLocalityIds);

                                      const currentLocs = getValues("locations") || [];
                                      const updatedLocs = [...currentLocs];
                                      if (updatedLocs[i]) updatedLocs[i] = { ...updatedLocs[i], localityIds: filteredLocalityIds, state: newState };
                                      const combined = Array.from(new Set(updatedLocs.flatMap(l => l?.localityIds || [])));
                                      setValue("localityIds", combined, { shouldValidate: true });
                                    }}
                                  >
                                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
                                    <SelectContent className="max-h-48">{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                  </Select>
                                )}
                              />
                            </div>
                          </div>

                          <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                              <FieldLabel required>
                                {i === 0 ? "Primary Locality / Area" : `Locality / Area (Location #${i + 1})`}
                              </FieldLabel>
                              <div className="mt-1.5">
                                <Controller
                                  control={control} name={`locations.${i}.localityIds`}
                                  rules={{ required: "Locality is required" }}
                                  render={({ field }) => {
                                    const locState = watch(`locations.${i}.state`);
                                    const locValueArr = watch(`locations.${i}.localityIds`) || field.value || [];
                                    const currentLocId = Array.isArray(locValueArr) && locValueArr.length > 0 ? String(locValueArr[0]) : (typeof locValueArr === "string" ? locValueArr : "");

                                    const stateLocalities = locState
                                      ? (masterLocalitiesData || []).filter((loc: any) => loc.state === locState).map((loc: any) => ({
                                        value: String(loc?.id ?? loc?.localityNumber ?? loc?.code ?? loc?.name ?? ""),
                                        label: `${loc.name || ''} (${loc.state || ''}${loc.city ? ' - ' + loc.city : ''})`
                                      }))
                                      : (masterLocalitiesData.length > 0 ? masterLocalitiesData : MASTER_LOCALITIES).map((loc: any) => ({
                                        value: (loc.id || loc.value || "").toString(),
                                        label: loc.label || `${loc.name} (${loc.state})`
                                      }));

                                    return (
                                      <SingleLocalitySelect
                                        value={currentLocId}
                                        placeholder={locState ? `Select locality in ${locState}...` : "Select locality..."}
                                        options={stateLocalities}
                                        onChange={(val: string) => {
                                          const newArr = val ? [val] : [];
                                          field.onChange(newArr);
                                          setValue(`locations.${i}.localityIds`, newArr);

                                          const currentLocs = getValues("locations") || [];
                                          const updatedLocs = [...currentLocs];
                                          if (updatedLocs[i]) updatedLocs[i] = { ...updatedLocs[i], localityIds: newArr };
                                          const combined = Array.from(new Set(updatedLocs.flatMap(l => l?.localityIds || [])));
                                          setValue("localityIds", combined, { shouldValidate: true });
                                        }}
                                      />
                                    );
                                  }}
                                />
                              </div>
                              {i === 0 && <FieldError message={errors.localityIds?.message as string} />}
                            </div>

                            <div>
                              <FieldLabel required>ZIP Code</FieldLabel>
                              <Input {...register(`locations.${i}.zipCode`, { required: "ZIP Code is required" })} placeholder="10001" maxLength={10} className="mt-1.5" />
                            </div>
                          </div>



                          <div className="grid sm:grid-cols-3 gap-3">
                            <div>
                              <FieldLabel>Location Phone</FieldLabel>
                              <div className="relative mt-1.5">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                <Input
                                  {...register(`locations.${i}.phone`, {
                                    pattern: { value: /^[^a-zA-Z]*$/, message: "Alphabets are not allowed" }
                                  })}
                                  onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                                  type="tel" placeholder="(212) 555-0100" className="pl-9"
                                />
                              </div>
                            </div>
                            <div>
                              <FieldLabel>Office Phone</FieldLabel>
                              <div className="relative mt-1.5">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                <Input
                                  {...register(`locations.${i}.officePhone`, {
                                    pattern: { value: /^[^a-zA-Z]*$/, message: "Alphabets are not allowed" }
                                  })}
                                  onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                                  type="tel" placeholder="(212) 555-0200" className="pl-9"
                                />
                              </div>
                            </div>
                            <div>
                              <FieldLabel>Fax</FieldLabel>
                              <Input
                                {...register(`locations.${i}.fax`, {
                                  pattern: { value: /^[^a-zA-Z]*$/, message: "Alphabets are not allowed" }
                                })}
                                onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                                type="tel" placeholder="(212) 555-0300" className="mt-1.5"
                              />
                            </div>
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                              <FieldLabel>Location Email</FieldLabel>
                              <div className="relative mt-1.5">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                                <Input {...register(`locations.${i}.email`)} type="email" placeholder="office@practice.com" className="pl-9" />
                              </div>
                            </div>
                            <div>
                              <FieldLabel>Website</FieldLabel>
                              <Input {...register(`locations.${i}.website`)} placeholder="https://drjanesmith.com" className="mt-1.5" />
                            </div>
                          </div>

                          {/* Office Hours / Schedules */}
                          <div className="space-y-3 pt-2 border-t border-border">
                            <FieldLabel>Office Hours</FieldLabel>
                            <div className="space-y-2">
                              {DEFAULT_SCHEDULES.map((sched, dayIndex) => (
                                <div key={sched.dayOfWeek} className="flex flex-col sm:flex-row sm:items-center justify-between p-2.5 rounded-xl border border-border bg-slate-50/50 gap-2">
                                  <div className="flex items-center justify-between sm:justify-start gap-4 sm:w-40">
                                    <span className="text-xs font-semibold">{sched.dayOfWeek}</span>
                                    <Controller
                                      control={control} name={`locations.${i}.schedules.${dayIndex}.isClosed`}
                                      render={({ field }) => (
                                        <div className="flex items-center gap-2">
                                          <input type="checkbox" id={`closed-${i}-${dayIndex}`} checked={field.value} onChange={field.onChange} className="rounded border-gray-300 text-primary focus:ring-primary" />
                                          <label htmlFor={`closed-${i}-${dayIndex}`} className="text-xs cursor-pointer text-muted-foreground">Closed</label>
                                        </div>
                                      )}
                                    />
                                  </div>
                                  {!watch(`locations.${i}.schedules.${dayIndex}.isClosed`) && (
                                    <div className="flex flex-1 items-center gap-2">
                                      <Controller
                                        control={control} name={`locations.${i}.schedules.${dayIndex}.startTime`}
                                        render={({ field }) => (
                                          <Select value={field.value} onValueChange={field.onChange}>
                                            <SelectTrigger className="w-full text-xs h-9"><SelectValue placeholder="Start Time" /></SelectTrigger>
                                            <SelectContent className="max-h-48">
                                              {TIME_OPTIONS.map(time => <SelectItem key={time} value={time} className="text-xs">{time}</SelectItem>)}
                                            </SelectContent>
                                          </Select>
                                        )}
                                      />
                                      <span className="text-muted-foreground text-xs">to</span>
                                      <Controller
                                        control={control} name={`locations.${i}.schedules.${dayIndex}.endTime`}
                                        render={({ field }) => (
                                          <Select value={field.value} onValueChange={field.onChange}>
                                            <SelectTrigger className="w-full text-xs h-9"><SelectValue placeholder="End Time" /></SelectTrigger>
                                            <SelectContent className="max-h-48">
                                              {TIME_OPTIONS.map(time => <SelectItem key={time} value={time} className="text-xs">{time}</SelectItem>)}
                                            </SelectContent>
                                          </Select>
                                        )}
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button" variant="outline"
                    className="w-full rounded-xl border-dashed h-11 text-sm"
                    onClick={() => addLoc({
                      isPrimary: false, buildingNo: "", address1: "", address2: "", city: "", state: "",
                      zipCode: "", phone: "", officePhone: "", fax: "", email: "", website: "", localityIds: [], schedules: [...DEFAULT_SCHEDULES],
                    })}

                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Another Location
                  </Button>
                </div>
              )}

              {/* ── Step 4: Fee Schedule ── */}
              {step === 4 && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between gap-4">
                    <SectionHeader icon={Percent} title="Fee Schedule Rates by Billing Code Type" subtitle="Configure Fee Schedule rates (% of Fee Schedule) globally or per practice location." />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-xs font-semibold shrink-0 rounded-xl border-primary/30 text-primary hover:bg-primary/5"
                      onClick={() => {
                        const name = prompt("Enter new Billing Code Type name (e.g., Radiology, Pathology, DME):");
                        if (name && name.trim()) {
                          const trimmed = name.trim();
                          setCategoryRates(prev => ({ ...prev, [trimmed]: 100 }));
                          const currentCodeTypes = form.getValues("codeTypes") || [];
                          if (!currentCodeTypes.includes(trimmed)) {
                            form.setValue("codeTypes", [...currentCodeTypes, trimmed]);
                          }
                        }
                      }}
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Billing Code Type
                    </Button>
                  </div>

                  {/* Practice Location Tabs for Registration Fee Schedule */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-muted-foreground">Select Practice Location to Configure Rates</Label>
                    <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                      <button
                        type="button"
                        onClick={() => setSelectedRateLocationIndex(-1)}
                        className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 shrink-0 border ${
                          selectedRateLocationIndex === -1
                            ? "bg-primary text-white border-primary shadow-xs"
                            : "bg-white hover:bg-slate-100 text-foreground border-border"
                        }`}
                      >
                        <Globe className="w-4 h-4" />
                        <span>All Locations (Global Default)</span>
                      </button>

                      {locFields.map((locItem: any, idx: number) => {
                        const isSelected = selectedRateLocationIndex === idx;
                        const label = locItem.address1 || locItem.street1 || `Location ${idx + 1}`;
                        const cityState = [locItem.city, locItem.state].filter(Boolean).join(", ");
                        const keys = getLocationKeys(locItem, idx);
                        const hasCustomOverrides = keys.some(k => categoryRates[k] && typeof categoryRates[k] === "object" && Object.keys(categoryRates[k]).length > 0);

                        return (
                          <button
                            key={locItem.id || idx}
                            type="button"
                            onClick={() => setSelectedRateLocationIndex(idx)}
                            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 shrink-0 border ${
                              isSelected
                                ? "bg-primary text-white border-primary shadow-xs"
                                : "bg-white hover:bg-slate-100 text-foreground border-border"
                            }`}
                          >
                            <MapPin className="w-3.5 h-3.5" />
                            <span>Location {idx + 1}: {label} {cityState ? `(${cityState})` : ''}</span>
                            {hasCustomOverrides && (
                              <span className={`w-2 h-2 rounded-full ${isSelected ? "bg-amber-300 animate-pulse" : "bg-emerald-500"}`} title="Custom location overrides configured" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {selectedRateLocationIndex !== -1 && (
                    <div className="bg-amber-500/10 border border-amber-500/20 text-amber-900 px-4 py-2.5 rounded-xl text-xs flex items-center justify-between">
                      <span className="flex items-center gap-1.5 font-medium">
                        <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        Configuring custom rate for <strong>Location {selectedRateLocationIndex + 1} ({locFields[selectedRateLocationIndex]?.address1 || 'Practice Location'})</strong>. Values set here override the global baseline.
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs font-bold text-amber-700 hover:bg-amber-500/20 shrink-0"
                        onClick={() => handleResetLocationRatesToDefault(selectedRateLocationIndex)}
                      >
                        Reset Overrides
                      </Button>
                    </div>
                  )}

                  {displayedCodeTypes.length > 0 ? (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {displayedCodeTypes.map((codeTypeObj: any, idx: number) => {
                        const codeTypeName = typeof codeTypeObj === "object" && codeTypeObj !== null
                          ? (codeTypeObj.name || codeTypeObj.code || String(codeTypeObj.id || idx))
                          : String(codeTypeObj || `CodeType-${idx}`);
                        const codeTypeId = codeTypeObj?.id || codeTypeName;
                        const { rate: currentVal, isCustom } = getEffectiveRateForCodeType(codeTypeName, codeTypeId);

                        return (
                          <div key={codeTypeId} className="bg-slate-50 border border-border rounded-xl p-4 space-y-3 shadow-2xs">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-foreground">{String(codeTypeName)}</span>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  isCustom 
                                    ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 font-bold" 
                                    : "bg-primary/5 text-primary border-primary/20"
                                }`}
                              >
                                {selectedRateLocationIndex === -1 ? "Global Rate %" : isCustom ? "Custom Loc Rate %" : "Inherited %"}
                              </Badge>
                            </div>
                            <div className="relative">
                              <Input
                                type="number"
                                min={1}
                                value={currentVal}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  handleUpdateCodeTypeRate(codeTypeName, codeTypeId, val);
                                }}
                                className="pr-8 h-10 text-sm font-mono bg-white"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">%</span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {selectedRateLocationIndex === -1 
                                ? `Applies to all ${codeTypeName} CPT codes across all practice locations as default baseline.` 
                                : `Applies to ${codeTypeName} CPT codes for Location ${selectedRateLocationIndex + 1}.`
                              }
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No billing code types selected. Go back to Step 2 to select Billing Code Types.</p>
                  )}
                </div>
              )}


              {/* ── Step 5: Online Presence ── */}
              {step === 5 && (
                <div className="space-y-6">
                  <SectionHeader icon={Globe} title="Online Presence" subtitle="Link your profiles to your account" />

                  {/* Profile Links */}
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-foreground">External Profile & Social Links</p>
                    <p className="text-xs text-muted-foreground -mt-2">Add URLs to your social media or external directory profiles</p>
                    
                    {socialFields.map((field, index) => (
                      <div key={field.id} className="flex flex-col sm:flex-row items-center gap-2.5 bg-muted/20 p-3 rounded-xl border border-border">
                        <div className="w-full sm:w-44">
                          <Controller
                            control={control}
                            name={`socialLinks.${index}.platform`}
                            render={({ field: pField }) => (
                              <Select value={pField.value || "Zocdoc"} onValueChange={pField.onChange}>
                                <SelectTrigger className="h-10 text-xs font-semibold rounded-xl bg-background">
                                  <SelectValue placeholder="Select Platform" />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl">
                                  {["Facebook", "Instagram", "Blog", "Zocdoc", "Healthgrades", "WebMD"].map((opt) => (
                                    <SelectItem key={opt} value={opt}>
                                      {opt}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>

                        <div className="flex-1 w-full">
                          <Controller
                            control={control}
                            name={`socialLinks.${index}.url`}
                            render={({ field: uField }) => (
                              <Input
                                {...uField}
                                placeholder="https://..."
                                className="h-10 text-xs rounded-xl"
                              />
                            )}
                          />
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSocial(index)}
                          className="h-10 w-10 text-destructive hover:bg-destructive/10 rounded-xl shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}

                    {socialFields.length === 0 && (
                      <p className="text-xs text-muted-foreground italic">No external profile or social links added yet. Click below to add one.</p>
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addSocial({ platform: "Zocdoc", url: "" })}
                      className="rounded-xl text-xs gap-1.5 border-dashed mt-2"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Profile / Social Link
                    </Button>
                  </div>

                  {/* Summary */}
                  <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
                    <p className="text-xs font-semibold text-primary mb-2 uppercase tracking-wide">Profile Summary</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div className="text-muted-foreground">Name</div>
                      <div className="font-medium truncate">{watch("displayName") || "—"}</div>
                      <div className="text-muted-foreground">NPI</div>
                      <div className="font-medium">{watch("npiNumber") || "—"}</div>
                      <div className="text-muted-foreground">Specialties</div>
                      <div className="font-medium">{watch("providerTypes").length > 0 ? watch("providerTypes").length + " selected" : "—"}</div>
                      <div className="text-muted-foreground">Locations</div>
                      <div className="font-medium">{watch("locations").length}</div>
                      <div className="text-muted-foreground">Credentials</div>
                      <div className="font-medium">{watch("credentials").length}</div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <div className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</div>

            <div className="flex items-center gap-3">
              <Button
                type="button" variant="outline"
                className="rounded-xl h-11 px-6"
                onClick={step === 0 ? () => setLocation("/register") : goBack}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {step === 0 ? "Back to Register" : "Back"}
              </Button>

              {step < STEPS.length - 1 ? (
                <Button
                  type="button"
                  className="rounded-xl h-11 px-6 bg-gradient-to-r from-primary to-secondary text-white"
                  onClick={goNext}
                  disabled={!isStepValid()}
                >
                  Continue <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  type="button"
                  className="rounded-xl h-11 px-6 bg-gradient-to-r from-primary to-secondary text-white"
                  onClick={onSubmit}
                  disabled={loading || !isStepValid()}
                >
                  {loading ? "Submitting…" : <><Check className="w-4 h-4 mr-1.5" />Submit Registration</>}
                </Button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ─── SectionHeader ──────────────────────────────────────────────────────── */

function SectionHeader({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-3 pb-4 border-b border-border">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="font-semibold text-lg">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}
